import {ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ValidationService} from '../../../services/validation/validation.service';
import {UtilService} from '../../../services/util/util.service';
import {AuthService} from '../../../services/auth/auth.service';
import {ViewService} from "../../../services/view/view.service";
import {debounceTime, Subject, Subscription} from 'rxjs';
import {Privileges} from '../../../modules/admin/privileges/privileges.constants';

export type NumericInputType = 'price' | 'percentage' | 'default';
export const NumericInputType: {Price: NumericInputType, Percentage: NumericInputType, Default: NumericInputType} = {
  Price: 'price' as NumericInputType,
  Percentage: 'percentage' as NumericInputType,
  Default: 'default' as NumericInputType
};

@Component({
  selector: 'app-numeric-input',
  templateUrl: './numeric-input.component.html',
  styleUrls: ['./numeric-input.component.scss']
})
export class NumericInputComponent implements OnInit, OnDestroy {
  @Input() public label: string;
  @Input() public placeholder: string;
  @Input() public ignoreMax32: boolean;
  @Input() public min: number;
  @Input() public max: number;
  @Input() public appClass: string | string[] | Set<string> | {[p: string]: any};
  @Input() public withPrefix: boolean;
  @Input() public withSuffix: boolean;
  @Input() public type: NumericInputType = NumericInputType.Default;
  @Input() public hideSymbol: boolean;
  @Input() public allowDot: boolean;
  @Input() public name: string;
  @Input() public hasCustomError: boolean;
  @Input() public customError: string;
  @Input() public withLinkButton: boolean;
  @Input() public linkButtonText: string = 'general.api_general_apply';
  @Input() public small: boolean;
  @Input() public allowPercentageDecimalPlaces: boolean;

  @Input() public required: boolean;
  @Input() public disabled: boolean;
  @Input() public changeDetector: ChangeDetectorRef;

  private _value: number | string;
  public get value(): number | string {
    return this._value;
  }
  @Input() public set value(newValue: number | string) {
    if (typeof newValue === 'string') {
      newValue = newValue.trim();
    }
    if (this.type === NumericInputType.Price || (this.type === NumericInputType.Percentage && this.allowPercentageDecimalPlaces)) {
      newValue = ValidationService.formatCommasFromString(newValue);
    }
    this._value = newValue;
  }
  @Output() public valueChange: EventEmitter<number | string> = new EventEmitter<number | string>();
  @Output() public isValueValid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public inputKeyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
  @Output() public inputFocus: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() public inputBlur: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() public linkButtonClicked: EventEmitter<any> = new EventEmitter<any>();

  @HostBinding('class') get hostClasses(): string {
    return (this.withPrefix || this.withSuffix) ? 'flex flex-flat' : '';
  }

  public validations: {valid: boolean, tooBig: boolean, tooSmall: boolean} = {
    valid: true,
    tooBig: false,
    tooSmall: false
  };
  private _debounceSub: Subscription;
  private _numberDebounce: Subject<number | string> = new Subject<number | string>();
  private _visited: boolean;

  constructor(public authService: AuthService,
              public utilService: UtilService,
              private validationService: ValidationService,
              private viewService: ViewService) {}

  public ngOnInit(): void {
    this.validateValue();
    this._debounceSub = this._numberDebounce.pipe(debounceTime(500)).subscribe(_ => {
      this.validateValue();
      if (this.changeDetector) {
        this.changeDetector.markForCheck();
      } else {
        this.viewService.triggerCdr();
      }
    });
  }
  public ngOnDestroy(): void {
    if (this._debounceSub) {
      this._debounceSub.unsubscribe();
      delete this._debounceSub;
    }
  }

  public validateValue(): void {
    this.validations.tooBig = this.isValueTooBig();
    this.validations.tooSmall = this.isValueTooSmall();
    this.validations.valid = this.validByType && !this.validations.tooBig && !this.validations.tooSmall && !this.hasCustomError;
    this.isValueValid.emit(this.validations.valid);
  }
  private get validByType(): boolean {
    switch (this.type) {
      case NumericInputType.Price:
        return ValidationService.validatePrice(this.value) || (!this.required && !this.value) ||
          (this.required && !this.value && !this._visited);
      case NumericInputType.Percentage:
        return ValidationService.validatePercentage(this.value, this.allowPercentageDecimalPlaces) || (!this.required && !this.value) ||
          (this.required && !this.value && !this._visited);
      default:
        return this.validationService.emptyOrNumeric(this.value, this.ignoreMax32);
    }
  }
  private isValueTooBig(): boolean {
    return Number(this.value) > Number.MAX_SAFE_INTEGER || (!!this.max && Number(this.value) > this.max);
  }
  private isValueTooSmall(): boolean {
    return Number(this.value) < 0 || (!!this.min && Number(this.value) < this.min);
  }

  public changeValue(): void {
    this.valueChange.emit(this._value);
    this._numberDebounce.next(this._value);
  }

  public inputFocused(focusEvent: FocusEvent): void {
    this.inputFocus.emit(focusEvent);
    this._visited = true;
  }
  public inputBlurred(blurEvent: FocusEvent): void {
    this.inputBlur.emit(blurEvent);
    this.validateValue();
  }

  protected readonly NumericInputType = NumericInputType;
  protected readonly Privileges = Privileges;
}
